import React from 'react'
import {I18n, Translate} from 'react-redux-i18n'

import {
  AssetGroup,
  calculateDailyReturn,
  PricesExtended,
  Signals,
  TradeCommandType,
  TradeObject,
  TradingAccount,
} from '@bdswiss/mt4-connector'
import {AccountDetails, AppMode, SignalType, TradeMode} from '../types'
import {getAvailableSymbols, getAvailableSymbolsGroups, getSignalsType} from '../utils/common-utils'

import './AssetsSelect.css'
import {SignalsDisclaimer} from './SignalsDisclaimer'
import {SimpleSignalsTable} from "./SimpleSignalsTable";
import {TelegramSignalsState} from "../reducers/telegramSignals";
import {AssetsTableFC} from "./AssetsTableFC";
import {TelegramSignalTable} from "./TelegramSignalsTable";

export interface Props {
  opened: boolean
  assets: PricesExtended
  account: TradingAccount
  accountType: AccountDetails['__typename']
  assetGroups: AssetGroup[]
  locale: string
  symbol: string
  favourites: string[]
  accountLoaded: boolean
  positions: TradeObject[]
  currency: string
  tradeOperationEnabled: boolean
  appMode: AppMode
  signals: Signals
  telegramSignals: TelegramSignalsState
  signal?: any
  dismissedRiscDisclaimer: boolean
  onSetTradingSymbol: (symbol: string, tradeType: TradeCommandType) => void
  onSetSignal: (signal: any) => void
  onClearSignal: () => void
  onToggleMenu: () => void
  onToggleFavourite: (favourites: string[]) => void
  onSetChartMode: () => void
  onSelectPosition: (order: number, expanded?: boolean) => void
  onCloseTrade: (order: number, uuid?: string) => void
  onSetAppMode: (mode: AppMode) => void
  onSetAssetGroup: (group: string) => void
  positionsSymbols: Array<string>
}

export interface State {
  assetGroups: AssetGroup[]
  assetGroup: string
  filter: string
  favourites: Map<string, { confirmed: boolean; operation: 'on' | 'off' }> // true if confirmed from server
}

const AssetsSelect = (props: Props) => {
  return <AssetsSelectContent {...props} />
}

class AssetsSelectContent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.onSearch = this.onSearch.bind(this)
    this.toggleAssetGroup = this.toggleAssetGroup.bind(this)
    this.toggleFavourite = this.toggleFavourite.bind(this)

    const { favourites: propFavourites, assetGroups: propAssetGroups, symbol, assets } = this.props

    const favourites = getAvailableSymbols(propFavourites, assets)
    const assetGroups = getAvailableSymbolsGroups(propAssetGroups, assets)

    const assetGroup = favourites.includes(symbol)
      ? { label: 'favourites' }
      : assetGroups.find(group => group.symbols.includes(symbol))

    this.state = {
      assetGroups,
      assetGroup: assetGroup ? assetGroup.label : favourites.length ? 'favourites' : 'all',
      favourites: favourites.reduce(
        (acc, cur) => acc.set(cur, { confirmed: true, operation: 'on' }),
        new Map()
      ),
      filter: '',
    }
  }

  public render() {
    const {
      assets,
      account,
      accountType,
      onToggleMenu,
      symbol,
      // accountLoaded,
      positions,
      // currency,
      // locale,
      // tradeOperationEnabled,
      // onSelectPosition,
      // onSetChartMode,
      // onCloseTrade,
      // onSetAppMode,
      // onSetTradingSymbol,
      signal,
      signals,
      // onSetSignal,
      dismissedRiscDisclaimer,
      positionsSymbols,
    } = this.props
    const { assetGroups, assetGroup, favourites, filter } = this.state
    const sortedSignals = Object.values(signals)
      .filter(signal => assetGroups.flatMap(group => group.symbols).includes(signal.symbol))
      .reduce((acc, signal) => ({ ...acc, [signal.id]: signal }), {})

    const signalsType = getSignalsType()
    const signalsSize = this.getSignalsCount(signalsType)
    let assetCategories = [
      <label
        key={'favourites'}
        onClick={() => this.toggleAssetGroup('favourites')}
        className={'btn' + (assetGroup === 'favourites' ? ' active' : '')}
        data-cy={'favourites'}
        title={favourites.size.toString()}
      >
        <input
          type="radio"
          name="options"
          id="option1"
          autoComplete="off"
          defaultChecked={assetGroup === 'favourites'}
        />
        <Translate value={'favourites'} />
        <span className="badge badge-pill badge-secondary">{favourites.size}</span>
      </label>,
      <label
        key={'all'}
        onClick={() => this.toggleAssetGroup('all')}
        className={'btn' + (assetGroup === 'all' ? ' active' : '')}
        data-cy={'all'}
      >
        <input
          type="radio"
          name="options"
          id="option1"
          autoComplete="off"
          defaultChecked={assetGroup === 'all'}
        />
        <Translate value={'all'} />
        <span className="badge badge-pill badge-secondary">
          {
            new Set(
              assetGroups
                .flatMap(group => group.symbols)
                .filter(
                  symbol =>
                    positionsSymbols.includes(symbol) ||
                    assets[symbol]?.['trade'] > TradeMode.onlyClose
                )
            ).size
          }
        </span>
      </label>,
    ]

    const tradingSignalsVisible = !accountType.includes('MT5') && getSignalsType() !== SignalType.None;

    if (tradingSignalsVisible) {
      assetCategories.splice(
      1,
      0,
      <label
        key={'signals'}
        onClick={() => this.toggleAssetGroup('signals')}
        className={'btn' + (assetGroup === 'signals' ? ' active' : '')}
        data-cy={'signals'}
        title={signalsSize.toString()}
      >
        <input
          type="radio"
          name="options"
          id="option1"
          autoComplete="off"
          defaultChecked={assetGroup === 'signals'}
        />
        <Translate value={'signals'} />
        <span className="badge badge-pill badge-secondary">{signalsSize}</span>
      </label>
    )
  }

    assetCategories = [
      ...assetCategories,
      ...assetGroups.map(group => {
        return (
          <label
            key={group.label}
            onClick={() => this.toggleAssetGroup(group.label)}
            className={'btn' + (assetGroup === group.label ? ' active' : '')}
            data-cy={group.label}
          >
            <input
              type="radio"
              name="options"
              id="option1"
              autoComplete="off"
              defaultChecked={assetGroup === group.label}
            />
            <Translate value={group.label} />
            <span className="badge badge-pill badge-secondary">
              {
                group.symbols.filter(
                  symbol =>
                    positionsSymbols.includes(symbol) ||
                    assets[symbol]?.['trade'] > TradeMode.onlyClose
                ).length
              }
            </span>
          </label>
        )
      }),
    ]
    let data: any[] = []
    switch (assetGroup) {
      case 'all':
        data = assetGroups
          .reduce((acc, cur) => {
            if (cur.label !== 'popular') {
              return acc.concat(cur.symbols)
            } else {
              return acc
            }
          }, [] as string[])
          .map(key => assets[key])
        break
      case 'favourites':
        data = Array.from(favourites.keys()).map(key => assets[key])
        break
      default:
        const activeGroup = assetGroups.find(group => group.label === assetGroup)
        data = activeGroup
          ? activeGroup.symbols.map(key => assets[key])
          : Array.from(favourites.keys()).map(key => assets[key])
    }
    data = data
      .filter(asset => positionsSymbols.includes(asset.symbol) || asset.trade > TradeMode.onlyClose)
      .map(asset => Object.assign(asset, { change: calculateDailyReturn('bid', asset) }))

    return (
      <div className="d-flex flex-row flex-fill overflow-auto">
        <div className="select-assets__group d-flex flex-column" data-toggle="buttons">
          {assetCategories}
        </div>
        <div className="d-flex flex-column flex-fill overflow-auto">
          <div className="select-assets__header d-flex align-items-center">
            <input
              value={filter}
              onChange={e => this.onSearch(e.target.value)}
              id="assetsSearchInput"
              type="text"
              placeholder={I18n.t('search')}
              className="search-input flex-fill mr-3"
              data-cy="assetSearch"
            />
            {/* <i onClick={e => this.onSearch()} className={`clear-assets-input icon-close ${(filter?'':'empty-filter')}`}/> */}
            <span
              onClick={e => this.onSearch()}
              className={`clear-assets-input ${filter ? '' : 'empty-filter'}`}
            >
              {I18n.t('clear')}
            </span>
            <button onClick={onToggleMenu} className="btn close_btn" data-cy="assetsClose">
              <i className="icon-close" />
            </button>
          </div>
          <div className="select-assets__content d-flex no-scrolling">
            { assetGroup === 'signals' && signalsType === SignalType.Simple && <SimpleSignalsTable
                signals={sortedSignals}
                signal={signal}
                favourites={favourites}
                assets={assets}
                positions={positions}
                filter={filter}
                onToggleFavourite={this.toggleFavourite}
              />}
            { assetGroup === 'signals' && signalsType === SignalType.Telegram && <TelegramSignalTable
                signal={signal}
                favourites={favourites}
                assets={assets}
                positions={positions}
                filter={filter}
            />}
              {/*// <SignalsTable*/}
              {/*//   assets={assets}*/}
              {/*//   signals={signals}*/}
              {/*//   signal={signal}*/}
              {/*//   favourites={favourites}*/}
              {/*//   locale={locale}*/}
              {/*//   accountLoaded={accountLoaded}*/}
              {/*//   positions={positions}*/}
              {/*//   currency={currency}*/}
              {/*//   onSetSignal={onSetSignal}*/}
              {/*//   onSetChartMode={onSetChartMode}*/}
              {/*//   onSelectPosition={onSelectPosition}*/}
              {/*//   onCloseTrade={onCloseTrade}*/}
              {/*//   onSetAppMode={onSetAppMode}*/}
              {/*//   onToggleFavourite={this.toggleFavourite}*/}
              {/*//   filter={filter}*/}
              {/*// />*/}
              {/*// <AssetsTable*/}
              {/*//   assetsData={data}*/}
              {/*//   assets={assets}*/}
              {/*//   assetGroups={assetGroups}*/}
              {/*//   assetGroup={assetGroup}*/}
              {/*//   symbol={symbol}*/}
              {/*//   locale={locale}*/}
              {/*//   accountLoaded={accountLoaded}*/}
              {/*//   positions={positions}*/}
              {/*//   currency={currency}*/}
              {/*//   tradeOperationEnabled={tradeOperationEnabled}*/}
              {/*//   favourites={favourites}*/}
              {/*//   signals={this.props.signals}*/}
              {/*//   signal={signal}*/}
              {/*//   filter={filter}*/}
              {/*//   onToggleFavourite={this.toggleFavourite}*/}
              {/*//   onSetTradingSymbol={onSetTradingSymbol}*/}
              {/*//   onSetSignal={onSetSignal}*/}
              {/*//   onSetChartMode={onSetChartMode}*/}
              {/*//   onSelectPosition={onSelectPosition}*/}
              {/*//   onCloseTrade={onCloseTrade}*/}
              {/*//   onSetAppMode={onSetAppMode}*/}
              {/*// />*/}
              { assetGroup !== 'signals' && <AssetsTableFC
                key={assetGroup}
                account={account}
                accountType={accountType}
                assetsData={data}
                assetGroup={assetGroup}
                assetGroups={assetGroups}
                symbol={symbol}
                positions={positions}
                signals={signals}
                favourites={favourites}
                filter={filter}
                onToggleFavourite={this.toggleFavourite}
              /> }
          </div>
          {!dismissedRiscDisclaimer &&
            !accountType.includes('MT5') &&
            (assetGroup === 'signals' || assetGroup === 'favourites') && getSignalsType() === SignalType.Autochartist && <SignalsDisclaimer />}
        </div>
      </div>
    )
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.favourites !== this.props.favourites) {
      const favourites = getAvailableSymbols(this.props.favourites, this.props.assets)
      this.setState(pState => ({
        favourites: favourites.reduce(
          (acc, cur) => acc.set(cur, { confirmed: true, operation: 'on' }),
          new Map()
        ),
      }))
    }
    if (prevProps.assetGroups !== this.props.assetGroups) {
      const { assets } = this.props
      this.setState(pState => ({
        assetGroups: getAvailableSymbolsGroups(this.props.assetGroups, assets),
      }))
    }
  }

  private toggleAssetGroup(assetGroup: string) {
    if (assetGroup !== 'signals') {
      this.props.onClearSignal()
    }
    this.setState(
      prevState => ({
        assetGroup,
      }),
      () => this.props.onSetAssetGroup(assetGroup)
    )
  }

  private toggleFavourite(favourite: string) {
    const { favourites } = this.state
    if (favourites.get(favourite)) {
      favourites.set(favourite, { confirmed: false, operation: 'off' })
    } else {
      favourites.set(favourite, { confirmed: false, operation: 'on' })
    }
    this.props.onToggleFavourite(
      Array.from(favourites.keys()).filter(key => favourites.get(key)!.operation === 'on')
    )
    this.setState(prevState => ({
      favourites,
    }))
  }

  private onSearch(filter?: string) {
    this.setState(prevState => ({
      assetGroup: prevState.assetGroup !== 'signals' ? 'all' : prevState.assetGroup,
      filter: filter || '',
    }))
  }

  private getSignalsCount(type: SignalType) {
    if(type === SignalType.Telegram){
      return this.props.telegramSignals.clientStatistics.usedSignals
    }
    if(type === SignalType.Simple){
      return Object.keys(this.props.signals).length
    }
    return 0;
  }

}

export default AssetsSelect
